import { useMutation, useQueryClient } from 'react-query';
import { groupsApi } from '../../../services/groups-api-client';
import { scheduledBroadcastsKey } from '../../../lib/groupQueryKeys';
import {
  classifyPlanError,
  deriveExcludedSteps,
  markAfterUnknownOutcome,
  planBodyOf,
  settlesPlanAttempt,
  stepBodyOf,
  unresolvedAttemptNote,
  type PlanRequest,
} from '../../../lib/reminderPlan';
import { clearUnresolvedPlan, markUnresolvedPlan, unresolvedPlan, updateUnresolvedPlan } from '../../../lib/reminderPlanGuard';
import type {
  GroupScheduledBroadcast,
  ReminderPlanResult,
  ReplaceReminderPlanResult,
  ScheduledSequenceCancelResult,
} from '../../../types/groups';

/**
 * The writes a reminder plan needs. A plan is created, and rescheduled, by ONE atomic call to the API (every
 * step or none), so a failure never leaves a half-made plan and every retry — same plan id, same steps — is
 * answered from what exists. Each write refreshes the shared scheduled-broadcasts list when it settles.
 */
/**
 * The key the plan-writing mutations share, so `useIsMutating` can tell a write is running, including one started
 * by a previous mount (react-query keeps a running mutation alive after its component unmounts).
 */
export const planWriteKey = (groupId: string) => ['reminder-plan-write', groupId];

/** How long a request the client gave up on may still be committing on an API that cannot be asked (no /abandon endpoint). */
export const LATE_COMMIT_WINDOW_MS = 5 * 60_000;

/**
 * Run an idempotent call. If its outcome is UNKNOWN (a timeout, a dropped connection, a 5xx), ask `alreadyThere`
 * whether the server already has the result (a re-read of the list) and use it; otherwise send the SAME call again —
 * same plan id, same steps — which the API answers from what exists, so it can never write twice.
 */
async function callSafely<T>(
  call: () => Promise<T>,
  alreadyThere: () => Promise<T | null>,
  onUnknown: () => void = () => undefined,
): Promise<T> {
  try {
    return await call();
  } catch (error) {
    if (classifyPlanError(error) !== 'unknown') throw error;
    onUnknown();
    const found = await alreadyThere().catch(() => null);
    if (found) return found;
    return call();
  }
}

export function useReminderPlanActions(groupId: string) {
  const queryClient = useQueryClient();
  const refresh = () => { queryClient.invalidateQueries(scheduledBroadcastsKey(groupId)); };
  const readList = () => queryClient.fetchQuery(
    scheduledBroadcastsKey(groupId),
    () => groupsApi.listScheduledBroadcasts(groupId),
    { staleTime: 0 },
  );
  const rowsOf = async (sequenceId: string): Promise<GroupScheduledBroadcast[]> =>
    (await readList()).filter((row) => row.sequenceId === sequenceId && row.status !== 'cancelled');
  const anyRowsOf = async (sequenceId: string): Promise<GroupScheduledBroadcast[]> =>
    (await readList()).filter((row) => row.sequenceId === sequenceId);

  /** Cancel every pending row of a plan (`DELETE .../sequences/{sequenceId}`). */
  const cancelSequence = useMutation<ScheduledSequenceCancelResult, unknown, string>(
    (sequenceId) => groupsApi.cancelScheduledBroadcastSequence(groupId, sequenceId),
    { onSettled: refresh },
  );

  /**
   * Create a whole plan in one call. The attempt (its id, anchor date and steps) is remembered BEFORE the call and forgotten only
   * when the outcome is SETTLED, so a closed dialog, a reload or a second click can never mint a new id for a plan that may have
   * landed. An unknown outcome — and any failure that does not prove what exists under the id (a 401/403/429, an odd status) — stays
   * remembered until it resolves (see `reminderPlanGuard`).
   */
  const createPlan = useMutation<
    ReminderPlanResult,
    unknown,
    { sequenceId: string; anchorDate: string; requests: PlanRequest[] },
    { retry: boolean }
  >(
    ({ sequenceId, anchorDate, requests }) => callSafely(
      () => {
        markUnresolvedPlan(groupId, { sequenceId, anchorDate, requests, lastSentAt: Date.now() });
        return groupsApi.createReminderPlan(groupId, planBodyOf(sequenceId, anchorDate, requests));
      },
      async () => {
        const rows = await rowsOf(sequenceId);
        return rows.length > 0 ? { alreadyExisted: true, rows } : null;
      },
      () => updateUnresolvedPlan(groupId, sequenceId, { unknownOutcome: true }),
    ),
    {
      mutationKey: planWriteKey(groupId),
      onMutate: ({ sequenceId, anchorDate, requests }) => {
        // An attempt that is already remembered when this call starts is a RETRY of one whose outcome was unknown.
        const retry = unresolvedPlan(groupId, sequenceId) !== null;
        markUnresolvedPlan(groupId, { sequenceId, anchorDate, requests, lastSentAt: Date.now() });
        return { retry };
      },
      onSuccess: (_result, { sequenceId }) => clearUnresolvedPlan(groupId, sequenceId),
      onError: (error, { sequenceId }, context) => {
        const afterUnknownOutcome = Boolean(context?.retry || unresolvedPlan(groupId, sequenceId)?.unknownOutcome);
        if (settlesPlanAttempt(error, { afterUnknownOutcome })) {
          clearUnresolvedPlan(groupId, sequenceId);
          return;
        }
        // Still open: remember that it is (an unknown call, or a refusal that proves nothing) and say why, so Create is never off unexplained.
        const unknown = classifyPlanError(error) === 'unknown';
        updateUnresolvedPlan(groupId, sequenceId, {
          unknownOutcome: afterUnknownOutcome || unknown || undefined,
          note: unresolvedAttemptNote(error, afterUnknownOutcome) ?? undefined,
        });
      },
      onSettled: refresh,
    },
  );

  /**
   * "Reschedule plan": ONE call that cancels the old plan's pending steps and creates the new plan in a single
   * transaction. `newSequenceId` is derived from the old plan and the new date, so two tabs (or a retry) send the same
   * call and the API answers the repeat `alreadyExisted`. Steps it will not recreate (sent, sending, in doubt, cancelled, failed or skipped)
   * come back in `excluded` with a reason. Nothing recreatable = 409 `nothing_to_reschedule`, and nothing changed.
   */
  const reschedule = useMutation<
    ReplaceReminderPlanResult,
    unknown,
    { oldSequenceId: string; newSequenceId: string; anchorDate: string; requests: PlanRequest[] }
  >(
    async ({ oldSequenceId, newSequenceId, anchorDate, requests }) => {
      let afterUnknown = false;
      try {
        return await callSafely(
          () => groupsApi.replaceReminderPlan(groupId, oldSequenceId, {
            newSequenceId,
            anchorDate,
            steps: requests.map((entry) => stepBodyOf(entry.request)),
          }),
          async () => {
            const everything = await readList();
            const rows = everything.filter((row) => row.sequenceId === newSequenceId && row.status !== 'cancelled');
            if (rows.length === 0) return null;
            // The answer was lost, so what the replace left out is re-derived from the old plan's rows (a step in doubt is still reported as such).
            const excluded = deriveExcludedSteps(
              everything.filter((row) => row.sequenceId === oldSequenceId), rows, requests.map((entry) => entry.step));
            return { alreadyExisted: true, rows, cancelled: 0, excluded, inFlight: 0 };
          },
          () => { afterUnknown = true; },
        );
      } catch (error) {
        // An answer to a RETRY says nothing about an earlier call that may still be committing: tag it, so the host is never told
        // "nothing was changed" on the strength of it.
        throw afterUnknown ? markAfterUnknownOutcome(error) : error;
      }
    },
    { mutationKey: planWriteKey(groupId), onSettled: refresh },
  );

  /**
   * "Discard" an unresolved create attempt. The server is asked (`/abandon`): it waits for any create of that id still in flight and then
   * either reports the plan's rows (it was saved after all) or records a tombstone, after which a request that commits late is refused.
   * The attempt is forgotten ONLY on that definitive answer: `/abandon` (abandoned, or its rows reported) is the settling step — never the 400 or
   * 503 a retry happened to get.
   *
   * An API without the endpoint (404/405) cannot be asked, and a request the client gave up on may still be committing, so there the
   * host must confirm and it is refused until `LATE_COMMIT_WINDOW_MS` after the last send; then a fresh read of the list decides.
   */
  const discardAttempt = async (
    attempt: { sequenceId: string; lastSentAt: number },
    options: { confirmedRisk?: boolean } = {},
  ): Promise<{ resolved: boolean; needsConfirm?: boolean; note: string }> => {
    try {
      const result = await groupsApi.abandonReminderPlan(groupId, attempt.sequenceId);
      clearUnresolvedPlan(groupId, attempt.sequenceId);
      refresh();
      if (result.abandoned) {
        return { resolved: true, note: 'Discarded. If the earlier request is still on its way, the server will refuse it.' };
      }
      const live = result.rows.filter((row) => row.status !== 'cancelled');
      return {
        resolved: true,
        note: live.length > 0
          ? 'That plan was saved after all: it is on the Scheduled sends list.'
          : 'That plan was saved and has since been cancelled: nothing more to do.',
      };
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 404 && status !== 405) {
        return { resolved: false, note: "Couldn't confirm with the server, so the earlier attempt is still remembered. Try again." };
      }
    }

    // The server has no /abandon: only a fresh read of the list can say, and only once a late commit is no longer plausible.
    const wait = attempt.lastSentAt + LATE_COMMIT_WINDOW_MS - Date.now();
    if (wait > 0) {
      return {
        resolved: false,
        note: `This server can't be asked to refuse the earlier request, and it may still be arriving. Retry the same plan instead, or wait ${Math.ceil(wait / 60_000)} more minute${wait > 60_000 ? 's' : ''} before discarding.`,
      };
    }
    if (!options.confirmedRisk) {
      return {
        resolved: false,
        needsConfirm: true,
        note: 'This server cannot be asked to refuse the earlier request. If it was only delayed, it could still create the plan later. Discard anyway?',
      };
    }
    let rows: GroupScheduledBroadcast[];
    try {
      rows = await anyRowsOf(attempt.sequenceId);
    } catch {
      return { resolved: false, note: "Couldn't check the Scheduled sends list, so the earlier attempt is still remembered. Try again." };
    }
    clearUnresolvedPlan(groupId, attempt.sequenceId);
    return {
      resolved: true,
      note: rows.length === 0
        ? 'Nothing had been saved — you can set the plan up again.'
        : rows.some((row) => row.status !== 'cancelled')
          ? 'That plan was saved after all: it is on the Scheduled sends list.'
          : 'That plan was saved and has since been cancelled: nothing more to do.',
    };
  };

  return { cancelSequence, createPlan, reschedule, discardAttempt, readList };
}
